import { CacheKey } from '@signage-player/data-store';
const LOCAL_STORAGE_KEY = 'sp:device-hash';
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const HEX64_REGEX = /^[0-9a-f]{64}$/;
const LEGACY_URL_ID_PARAM = 'id';
function normalizeId(raw) {
    return raw.trim().toLowerCase();
}
function isValidInstanceId(id) {
    return UUID_REGEX.test(id) || HEX64_REGEX.test(id);
}
/** A 64-hex id means we are viewing an existing physical (Electron) device. */
export function isObserverInstance(id = resolveInstanceId()) {
    return HEX64_REGEX.test(normalizeId(id));
}
const KNOWN_INSTANCES_KEY = 'sp:known-instances';
const MAX_KNOWN_INSTANCES = 10;
// A slot that never paired is an abandoned pairing screen: forget it after a week idle.
const UNPAIRED_TTL_MS = 7 * 24 * 60 * 60 * 1000;
/**
 * `crypto.randomUUID` is only exposed in secure contexts (HTTPS / localhost).
 * `crypto.getRandomValues` works on plain http://* origins too; build the v4
 * UUID by hand so the web port works on insecure dev origins.
 */
function randomUUID() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0'));
    return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex.slice(6, 8).join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10, 16).join('')}`;
}
let resolved = null;
/**
 * Per-tab instance identity: URL hash #/<id> (or legacy ?id=) wins, then
 * last-used id in localStorage (bare-URL relaunch convenience), then a fresh
 * UUIDv4. Written back to both the URL (refresh keeps identity, address bar
 * is copy-pasteable) and localStorage. Memoized for the page's lifetime.
 */
export function resolveInstanceId() {
    if (resolved)
        return resolved;
    let id = readUrlId();
    if (id) {
        writeStorageId(id);
        writeUrlId(id);
    }
    else {
        id = readStorageId();
        if (id) {
            writeUrlId(id);
        }
        else {
            id = randomUUID();
            writeStorageId(id);
            writeUrlId(id);
        }
    }
    touchKnownInstance(id);
    resolved = id;
    return id;
}
export function __resetInstanceIdForTests() {
    resolved = null;
}
export function getKnownInstances() {
    const registry = readRegistry();
    const live = pruneExpiredUnpaired(registry);
    if (live.length !== registry.length)
        saveRegistry(live);
    return live.map(e => ({
        id: e.id,
        lastUsedAt: e.lastUsedAt,
        createdAt: e.createdAt,
        note: e.note ?? null,
        name: deriveName(e.id),
        displays: deriveDisplays(e.id),
    }));
}
/**
 * Display label per instance id: note, else backend name, else `fallback`.
 * Duplicates get " (2)", " (3)", … numbered in creation order, so a newly
 * created instance takes the next number instead of reshuffling the others as
 * last-used order changes.
 */
export function getInstanceLabels(instances, fallback) {
    const groups = new Map();
    for (const inst of instances) {
        const base = inst.note ?? inst.name ?? fallback;
        const group = groups.get(base);
        if (group)
            group.push(inst);
        else
            groups.set(base, [inst]);
    }
    const taken = new Set(groups.keys());
    const labels = new Map();
    for (const [base, group] of groups) {
        const ordered = [...group].sort((a, b) => a.createdAt - b.createdAt);
        labels.set(ordered[0].id, base);
        for (const inst of ordered.slice(1)) {
            // Skip numbers another instance already carries as its own name.
            let n = 2;
            while (taken.has(`${base} (${n})`))
                n++;
            const label = `${base} (${n})`;
            taken.add(label);
            labels.set(inst.id, label);
        }
    }
    return labels;
}
export function peekUrlInstanceId() {
    return readUrlId();
}
export function generateInstanceId() {
    return randomUUID();
}
export function setInstanceNote(id, note) {
    const registry = readRegistry();
    const entry = registry.find(e => e.id === id);
    if (!entry)
        return;
    const trimmed = note?.trim();
    if (trimmed)
        entry.note = trimmed;
    else
        delete entry.note;
    saveRegistry(registry);
}
/**
 * Forgets the instance locally: registry entry plus the whole sp:<id>:*
 * namespace (pairing/auth included) — same wipe pruning does. The slot
 * needs re-pairing if recreated.
 */
export function removeKnownInstance(id) {
    saveRegistry(readRegistry().filter(e => e.id !== id));
    forgetInstance(id);
}
// "#/<id>", "#<id>", or "#/<id>/<displayId>"
function readHashSegments() {
    return window.location.hash.replace(/^#\/?/, '').split('/').filter(Boolean);
}
function readHashId() {
    return readHashSegments()[0] ?? null;
}
/** Display DB id from the URL hash (#/<id>/<displayId>), or null. Side-effect-free. */
export function peekUrlDisplayId() {
    try {
        const seg = readHashSegments()[1];
        return seg && /^\d+$/.test(seg) ? Number(seg) : null;
    }
    catch {
        return null;
    }
}
function readUrlId() {
    try {
        const raw = readHashId() ?? new URLSearchParams(window.location.search).get(LEGACY_URL_ID_PARAM);
        if (!raw)
            return null;
        const id = normalizeId(raw);
        return isValidInstanceId(id) ? id : null;
    }
    catch {
        return null;
    }
}
function readStorageId() {
    try {
        const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
        if (!raw)
            return null;
        const id = normalizeId(raw);
        return isValidInstanceId(id) ? id : null;
    }
    catch {
        return null;
    }
}
function writeStorageId(id) {
    try {
        localStorage.setItem(LOCAL_STORAGE_KEY, id);
    }
    catch { /* ignore */ }
}
/** Current page URL with `#/<id>[/<displayId>]` as the identity; legacy ?id= dropped. */
export function formatInstanceUrl(id, displayId = null) {
    const url = new URL(window.location.href);
    url.hash = displayId !== null ? `/${id}/${displayId}` : `/${id}`;
    url.searchParams.delete(LEGACY_URL_ID_PARAM);
    return url.toString();
}
/** Current page URL stripped of any instance identity (hash and legacy ?id=). */
export function formatBareUrl() {
    const url = new URL(window.location.href);
    url.hash = '';
    url.searchParams.delete(LEGACY_URL_ID_PARAM);
    return url.toString();
}
// Canonicalizes in place (legacy ?id=, case, junk display segment). Keeps
// history.state: the web picker marks the entry it pushed and reads it back.
function writeUrlId(id, displayId = peekUrlDisplayId()) {
    try {
        const next = formatInstanceUrl(id, displayId);
        if (next === window.location.href)
            return;
        window.history.replaceState(window.history.state, '', next);
    }
    catch { /* ignore */ }
}
function readRegistry() {
    try {
        const parsed = JSON.parse(localStorage.getItem(KNOWN_INSTANCES_KEY) ?? '[]');
        if (!Array.isArray(parsed))
            return [];
        return parsed
            .filter((e) => !!e && typeof e.id === 'string' && isValidInstanceId(normalizeId(e.id)) && typeof e.lastUsedAt === 'number')
            // The guard doesn't validate note/createdAt — strip non-string notes here.
            // Legacy mixed-case ids are lowercased so lookups match the normalized
            // resolved id; entries written before createdAt existed adopt lastUsedAt
            // (persisted by the next save, so the order stays put afterwards).
            .map(({ id, lastUsedAt, createdAt, note }) => {
            const entry = {
                id: normalizeId(id),
                lastUsedAt,
                createdAt: typeof createdAt === 'number' ? createdAt : lastUsedAt,
            };
            if (typeof note === 'string')
                entry.note = note;
            return entry;
        });
    }
    catch {
        return [];
    }
}
function saveRegistry(instances) {
    try {
        localStorage.setItem(KNOWN_INSTANCES_KEY, JSON.stringify(instances));
    }
    catch { /* ignore */ }
}
function touchKnownInstance(id) {
    const registry = readRegistry();
    const existing = registry.find(e => e.id === id);
    const now = Date.now();
    // The id being resolved is never expired: an explicit URL visit revives it.
    const rest = pruneExpiredUnpaired(registry.filter(e => e.id !== id), now);
    rest.unshift({ ...existing, id, lastUsedAt: now, createdAt: existing?.createdAt ?? now });
    // Cap the registry; clear pruned instances' namespaced keys so orphaned
    // namespaces don't leak quota.
    for (const pruned of rest.splice(MAX_KNOWN_INSTANCES)) {
        forgetInstance(pruned.id);
    }
    saveRegistry(rest);
}
function pruneExpiredUnpaired(registry, now = Date.now()) {
    return registry.filter(e => {
        if (now - e.lastUsedAt <= UNPAIRED_TTL_MS || isPairedInstance(e.id))
            return true;
        forgetInstance(e.id);
        return false;
    });
}
// AuthService's rule: paired = cached device-by-hash carries a tenant server. Read
// raw so a TTL-expired entry still counts; device data (only cached once paired)
// covers a quota-swept one, but never outranks an auth entry that says unpaired.
function isPairedInstance(id) {
    try {
        const auth = localStorage.getItem(`sp:${id}:${CacheKey.Auth}`);
        if (auth !== null) {
            return !!JSON.parse(auth)?.value?.server?.id;
        }
    }
    catch { /* unreadable auth entry — fall through to device data */ }
    try {
        return localStorage.getItem(`sp:${id}:${CacheKey.DeviceData}`) !== null;
    }
    catch {
        return false;
    }
}
function forgetInstance(id) {
    removeNamespace(id);
    // Drop the last-used pointer too, or a bare-URL visit resurrects the id.
    try {
        if (localStorage.getItem(LOCAL_STORAGE_KEY) === id)
            localStorage.removeItem(LOCAL_STORAGE_KEY);
    }
    catch { /* ignore */ }
}
function removeNamespace(id) {
    try {
        const prefix = `sp:${id}:`;
        const stale = [];
        for (let i = 0; i < localStorage.length; i++) {
            const k = localStorage.key(i);
            if (k && k.startsWith(prefix))
                stale.push(k);
        }
        for (const k of stale)
            localStorage.removeItem(k);
    }
    catch { /* ignore */ }
}
// Name lives on the device's display records inside the cached deviceData
// envelope; derived at read time so backend renames show up after sync.
function deriveName(id) {
    try {
        const raw = localStorage.getItem(`sp:${id}:${CacheKey.DeviceData}`);
        if (!raw)
            return null;
        const device = JSON.parse(raw)?.value;
        const name = device?.primary_display?.name ?? device?.displays?.[0]?.name;
        return typeof name === 'string' && name ? name : null;
    }
    catch {
        return null;
    }
}
function deriveDisplays(id) {
    try {
        const raw = localStorage.getItem(`sp:${id}:${CacheKey.DeviceData}`);
        if (!raw)
            return [];
        const displays = JSON.parse(raw)?.value?.displays;
        if (!Array.isArray(displays))
            return [];
        return displays
            .filter((d) => !!d && typeof d.id === 'number')
            .map(d => ({
            id: d.id,
            name: typeof d.name === 'string' && d.name ? d.name : null,
            orientation: typeof d.orientation === 'string' && d.orientation ? d.orientation : null,
        }));
    }
    catch {
        return [];
    }
}
//# sourceMappingURL=instanceId.js.map